// src/pages/BrandManagement/components/BrandForm.tsx
import React, { useEffect, useState } from 'react';
import { Row, Col, FormGroup, Label, Input, Button, FormFeedback } from 'reactstrap';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

// Components
import LogoUploader from './LogoUploader';

// Services
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';

// Redux
import { uploadLogo, deleteLogo } from 'slices/brandManagement/thunk';

// Types
import { BrandInfo, BrandFormData } from '../types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('brand_management');

interface BrandFormProps {
  initialData: BrandInfo | null;
  onSave: (data: BrandFormData) => void;
  loading: boolean;
  // Bắn giá trị form hiện tại (kể cả chưa lưu) lên parent để panel "Xem trước" cập nhật ngay khi
  // gõ, thay vì chỉ đọc brandInfo đã lưu trong Redux (mục UAT-ADM-033 — trước đây preview đứng yên
  // cho tới khi bấm "Lưu thay đổi" thành công).
  onLiveChange?: (values: BrandFormData) => void;
}

// Component con để dùng useEffect theo dõi `values` bên trong closure của Formik render-prop —
// Formik (component form, không phải hook useFormik) không tự expose values ra ngoài render-prop.
const FormValuesSync: React.FC<{ values: BrandFormData; onChange?: (values: BrandFormData) => void }> = ({
  values,
  onChange,
}) => {
  useEffect(() => {
    onChange?.(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.companyName, values.companyShortName, values.primaryColor, values.secondaryColor]);
  return null;
};

// Validation schema
const brandSchema = Yup.object().shape({
  companyName: Yup.string()
    .required(st('vui_long_nhap_ten_cong_ty_error'))
    .max(200, st('ten_cong_ty_khong_duoc_qua_error')),
  companyShortName: Yup.string()
    .required(st('vui_long_nhap_ten_viet_tat_error'))
    .max(10, st('ten_viet_tat_khong_duoc_qua_error')),
  primaryColor: Yup.string()
    .required(st('vui_long_chon_mau_chu_dao_error'))
    .matches(/^#[0-9A-F]{6}$/i, st('ma_mau_phai_dung_quy_chuan_error')),
  secondaryColor: Yup.string()
    .required(st('vui_long_chon_mau_phu_error'))
    .matches(/^#[0-9A-F]{6}$/i, st('ma_mau_phai_dung_quy_chuan_error')),
});

const BrandForm: React.FC<BrandFormProps> = ({
  initialData,
  onSave,
  loading,
  onLiveChange,
}) => {
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const { uploading } = useSelector((state: any) => state.BrandManagement);
  
  // Initial values
  const initialValues: BrandFormData = {
    companyName: initialData?.companyName || '',
    companyShortName: initialData?.companyShortName || '',
    primaryColor: initialData?.primaryColor || '#405189',
    secondaryColor: initialData?.secondaryColor || '#3577f1',
  };
  
  // Xử lý upload logo
  const handleLogoUpload = async (file: File) => {
    try {
      await dispatch(uploadLogo(file)).unwrap();
      toast.success(st('upload_logo_thanh_cong_message'));
    } catch (err: any) {
      toast.error(err || st('khong_the_upload_logo_message'));
    }
  };
  
  // Xử lý xóa logo
  const handleLogoDelete = async () => {
    try {
      await dispatch(deleteLogo()).unwrap();
      toast.success(st('xoa_logo_thanh_cong_message'));
    } catch (err: any) {
      toast.error(err || st('khong_the_xoa_logo_message'));
    }
  };
  
  // Xử lý reset về màu mặc định
  const handleResetColors = (setFieldValue: any) => {
    setFieldValue('primaryColor', '#405189');
    setFieldValue('secondaryColor', '#3577f1');
    toast.info(st('da_dat_lai_mau_mac_dinh_message'));
  };
  
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={brandSchema}
      onSubmit={onSave}
      enableReinitialize
    >
      {({ errors, touched, values, setFieldValue }) => (
        <Form>
          <FormValuesSync values={values} onChange={onLiveChange} />
          {/* Logo Upload */}
          <div className="mb-4">
            <Label>{st('logo_label')} <span className="text-danger">*</span></Label>
            <LogoUploader
              currentLogoUrl={initialData?.logoUrl}
              onUpload={handleLogoUpload}
              onDelete={handleLogoDelete}
              uploading={uploading}
            />
          </div>
          
          <hr className="my-4" />
          
          {/* Company Name & Short Name */}
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label htmlFor="companyName">
                  {st('ten_cong_ty_label')} <span className="text-danger">*</span>
                </Label>
                <Field
                  name="companyName"
                  type="text"
                  className={`form-control ${
                    errors.companyName && touched.companyName ? 'is-invalid' : ''
                  }`}
                  placeholder={st('vietlottery_co_ltd_placeholder')}
                  data-action-code="input-company-name"
                  data-action-name="Tên công ty"
                  disabled={!PageActionPermission.canEnable(location.pathname, "input-company-name")}
                />
                {errors.companyName && touched.companyName && (
                  <FormFeedback type="invalid">{errors.companyName}</FormFeedback>
                )}
                <small className="text-muted">{st('ten_day_du_cua_cong_ty_hint')}</small>
              </FormGroup>
            </Col>
            
            <Col md={6}>
              <FormGroup>
                <Label htmlFor="companyShortName">
                  {st('ten_viet_tat_label')} <span className="text-danger">*</span>
                </Label>
                <Field
                  name="companyShortName"
                  type="text"
                  className={`form-control ${
                    errors.companyShortName && touched.companyShortName ? 'is-invalid' : ''
                  }`}
                  placeholder={st('vl_placeholder')}
                  maxLength={10}
                  data-action-code="input-company-short-name"
                  data-action-name="Tên viết tắt"
                  disabled={!PageActionPermission.canEnable(location.pathname, "input-company-short-name")}
                />
                {errors.companyShortName && touched.companyShortName && (
                  <FormFeedback type="invalid">{errors.companyShortName}</FormFeedback>
                )}
                <small className="text-muted">{st('toi_da_10_ky_tu_hint')}</small>
              </FormGroup>
            </Col>
          </Row>
          
          <hr className="my-4" />
          
          {/* Brand Colors */}
          <Row>
            {/* Primary Color */}
            <Col md={6}>
              <FormGroup>
                <Label htmlFor="primaryColor">
                  {st('mau_thuong_hieu_chu_dao_label')} <span className="text-danger">*</span>
                </Label>
                <div className="d-flex align-items-center mb-2">
                  <Input
                    type="color"
                    name="primaryColor"
                    data-action-code="input-primary-color-picker"
                    data-action-name="Màu chủ đạo (bảng chọn màu)"
                    value={values.primaryColor}
                    onChange={(e) => setFieldValue('primaryColor', e.target.value)}
                    style={{ width: '60px', height: '38px' }}
                    className="me-2"
                    disabled={!PageActionPermission.canEnable(location.pathname, "input-primary-color-picker")}
                  />
                  <Field
                    name="primaryColor"
                    type="text"
                    className={`form-control ${
                      errors.primaryColor && touched.primaryColor ? 'is-invalid' : ''
                    }`}
                    placeholder="#405189"
                    data-action-code="input-primary-color-hex"
                    data-action-name="Màu chủ đạo (mã hex)"
                    disabled={!PageActionPermission.canEnable(location.pathname, "input-primary-color-hex")}
                  />
                </div>
                <div
                  className="w-100 text-center fw-medium text-white"
                  style={{
                    height: '50px',
                    backgroundColor: values.primaryColor,
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  }}
                >
                  {st('mau_chu_dao_text')}
                </div>
                {errors.primaryColor && touched.primaryColor && (
                  <div className="text-danger small mt-1">{errors.primaryColor}</div>
                )}
                <small className="text-muted">{st('su_dung_cho_header_buttons_links_hint')}</small>
              </FormGroup>
            </Col>
            
            {/* Secondary Color */}
            <Col md={6}>
              <FormGroup>
                <Label htmlFor="secondaryColor">
                  {st('mau_phu_label')} <span className="text-danger">*</span>
                </Label>
                <div className="d-flex align-items-center mb-2">
                  <Input
                    type="color"
                    name="secondaryColor"
                    data-action-code="input-secondary-color-picker"
                    data-action-name="Màu phụ (bảng chọn màu)"
                    value={values.secondaryColor}
                    onChange={(e) => setFieldValue('secondaryColor', e.target.value)}
                    style={{ width: '60px', height: '38px' }}
                    className="me-2"
                    disabled={!PageActionPermission.canEnable(location.pathname, "input-secondary-color-picker")}
                  />
                  <Field
                    name="secondaryColor"
                    type="text"
                    className={`form-control ${
                      errors.secondaryColor && touched.secondaryColor ? 'is-invalid' : ''
                    }`}
                    placeholder={st('3577f1_placeholder')}
                    data-action-code="input-secondary-color-hex"
                    data-action-name="Màu phụ (mã hex)"
                    disabled={!PageActionPermission.canEnable(location.pathname, "input-secondary-color-hex")}
                  />
                </div>
                <div
                  className="w-100 text-center fw-medium text-white"
                  style={{
                    height: '50px',
                    backgroundColor: values.secondaryColor,
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  }}
                >
                  {st('mau_phu_label')}
                </div>
                {errors.secondaryColor && touched.secondaryColor && (
                  <div className="text-danger small mt-1">{errors.secondaryColor}</div>
                )}
                <small className="text-muted">{st('mau_bo_sung_cho_accents_hint')}</small>
              </FormGroup>
            </Col>
          </Row>
          
          <hr className="my-4" />
          
          {/* Action Buttons */}
          <div className="text-end">
            {PageActionPermission.canDisplay(location.pathname, "button-reset-colors") && (
              <Button
                type="button"
                color="light"
                className="me-2"
                data-action-code="button-reset-colors"
                data-action-name="Đặt lại màu mặc định"
                onClick={() => handleResetColors(setFieldValue)}
                disabled={loading}
              >
                <i className="ri-refresh-line me-1"></i>
                {st('dat_lai_mac_dinh_button')}
              </Button>
            )}
            {PageActionPermission.canDisplay(location.pathname, "button-submit") && (
              <Button
                type="submit"
                color="primary"
                data-action-code="button-submit"
                data-action-name="Lưu thay đổi thương hiệu"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    {st('dang_luu_text')}
                  </>
                ) : (
                  <>
                    <i className="ri-save-line me-1"></i>
                    {st('luu_thay_doi_text')}
                  </>
                )}
              </Button>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default BrandForm;